import { z } from 'zod';
import { EntityNode } from './entities.js';
export const GraphEdge = z.object({
    source: z.string(),
    target: z.string(),
    type: z.string(),
    props: z.record(z.string(), z.unknown()).default({}),
});
export const GraphData = z.object({
    nodes: z.array(EntityNode),
    edges: z.array(GraphEdge),
});
export const StatsResponse = z.object({
    entities: z.number(),
    documents: z.number(),
    relationships: z.number(),
    byLabel: z.array(z.object({ label: z.string().nullable(), count: z.number() })),
    topRelationships: z.array(z.object({ rel: z.string(), count: z.number() })),
});
export const SubgraphQuery = z.object({
    entity: z.string().min(1),
    hops: z.number().int().min(1).max(4).default(2),
});
/**
 * One entity rendered on the `/map` analyst surface. Coordinates are
 * the entity's primary location (set during ingest from the corpus's
 * \`entity_locations\` overlay). \`years\` is the de-duped set of years
 * extracted from this entity's incident relationship props (year /
 * start / end) — drives the time-range filter so an analyst can show
 * "everyone active 2018-2023" even when entities themselves are
 * timeless.
 */
export const MapEntity = z.object({
    name: z.string().min(1),
    label: z.string(),
    lat: z.number().min(-90).max(90),
    lng: z.number().min(-180).max(180),
    place: z.string().optional(),
    years: z.array(z.number().int()).optional(),
});
export const MapEntitiesResponse = z.object({
    entities: z.array(MapEntity),
});
export const CypherRequest = z.object({
    query: z.string().min(1),
});
/**
 * Ops-surface health check for the Neo4j graph. Returned by
 * `GET /api/admin/graph-health` and emitted by `pnpm neo4j:probe`.
 *
 * `ok: false` is possible even when counts are populated — it indicates
 * the last check failed in a way we caught (e.g. the `dbms.components()`
 * call for server version was rejected by a managed-tier Aura instance).
 * The counts come from separate queries, so partial results are
 * meaningful and worth returning rather than erroring out.
 */
export const GraphHealth = z.object({
    ok: z.boolean(),
    entities: z.number().int().nonnegative(),
    documents: z.number().int().nonnegative(),
    relationships: z.number().int().nonnegative(),
    byLabel: z.array(z.object({ label: z.string().nullable(), count: z.number().int() })),
    topRelationships: z.array(z.object({ rel: z.string(), count: z.number().int() })),
    server: z.object({
        name: z.string().optional(),
        version: z.string().optional(),
        edition: z.string().optional(),
        uri: z.string().optional(),
    }),
    latencyMs: z.number().int().nonnegative(),
    checkedAt: z.string(),
    error: z.string().optional(),
});
